import { Link } from 'react-router-dom';
import type { ThemePreset } from '@ugc/shared';
import { THEME_PRESETS } from '@ugc/shared';
import { useAuth } from '../auth/AuthContext';
import { getConfig } from '../config';
import { useGoogleFonts } from '../theme/useThemeVars';

/**
 * Three presets is enough to show that the look changes without turning the
 * page into a swatch catalogue.
 */
const SHOWCASE_IDS: readonly string[] = ['ivory', 'noir', 'sage'];

interface Step {
  number: string;
  title: string;
  body: string;
}

const STEPS: readonly Step[] = [
  {
    number: '01',
    title: 'Make an account',
    body: 'Email and a password. Then pick the link people will type: the address is yours from the first minute.',
  },
  {
    number: '02',
    title: 'Upload your work',
    body: 'Drop in vertical clips and photos straight from your phone. Sort them into sections you name yourself.',
  },
  {
    number: '03',
    title: 'Publish it',
    body: 'Choose a look, check the live preview, and hit publish. Change your mind later and the link stays the same.',
  },
];

/**
 * The product's own front door. Everything shown here is invented — the sample
 * tiles are theme presets with placeholder copy, never a real creator's page.
 */
export function LandingPage() {
  const { status } = useAuth();
  const signedIn = status === 'authenticated';
  const host = getConfig().siteOrigin.replace(/^https?:\/\//i, '');

  const showcase = SHOWCASE_IDS.map((id) => THEME_PRESETS.find((preset) => preset.id === id)).filter(
    (preset): preset is ThemePreset => preset !== undefined,
  );

  return (
    <div className="app-shell">
      <style>{LANDING_CSS}</style>

      <header className="app-header">
        <div className="container app-header__inner">
          <Link className="app-brand" to="/">
            Portfolio
          </Link>
          <nav className="app-nav">
            {signedIn ? (
              <Link className="btn btn--primary btn--sm" to="/app">
                Open my editor
              </Link>
            ) : (
              <>
                <Link className="btn btn--ghost btn--sm" to="/login">
                  Log in
                </Link>
                <Link className="btn btn--primary btn--sm" to="/signup">
                  Get started
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main className="app-main">
        <section className="container hero">
          <p className="eyebrow">For UGC creators</p>
          <h1 className="hero__title">One link that shows brands what you make.</h1>
          <p className="hero__subtitle">
            Upload your vertical videos and photos, group them the way you pitch them, and publish a
            portfolio page at <span className="mono">{host}/your-name</span>. No site builder, no
            template wrestling, no monthly design project.
          </p>
          <div className="btn-row">
            {signedIn ? (
              <Link className="btn btn--primary btn--lg" to="/app">
                Open my editor
              </Link>
            ) : (
              <>
                <Link className="btn btn--primary btn--lg" to="/signup">
                  Create your page
                </Link>
                <Link className="btn btn--secondary btn--lg" to="/login">
                  I already have one
                </Link>
              </>
            )}
          </div>
        </section>

        <section className="container section">
          <h2 className="card__title">Three steps</h2>
          <div className="grid grid--3" style={{ marginTop: 'var(--app-space-4)' }}>
            {STEPS.map((step) => (
              <article key={step.number} className="feature">
                <p className="eyebrow">{step.number}</p>
                <h3 className="feature__title">{step.title}</h3>
                <p className="muted text-sm">{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="container section">
          <h2 className="card__title">Pick a look, change it whenever</h2>
          <p className="lead" style={{ marginTop: 'var(--app-space-2)' }}>
            Every page starts from a preset. Colours, type and spacing are yours to nudge, and the
            preview updates as you go.
          </p>
          <div className="landing-showcase">
            {showcase.map((preset) => (
              <SampleTile key={preset.id} preset={preset} />
            ))}
          </div>
          <p className="hint">Sample pages. Names, links and images are placeholders.</p>
        </section>

        <section className="container section">
          <h2 className="card__title">What it does not do</h2>
          <ul className="stack stack--sm landing-honest">
            <li>It is one page, not a multi-page website or a blog.</li>
            <li>It hosts your media and shows it. It does not send pitches or find you work.</li>
            <li>Analytics are not part of it yet.</li>
          </ul>
        </section>

        {!signedIn ? (
          <section className="container section">
            <div className="card">
              <div className="card__body stack">
                <h2 className="card__title">Ready when you are</h2>
                <p className="muted">
                  Claim your link now — you can upload and publish whenever the work is ready.
                </p>
                <div>
                  <Link className="btn btn--primary btn--lg" to="/signup">
                    Create your page
                  </Link>
                </div>
              </div>
            </div>
          </section>
        ) : null}
      </main>

      <footer className="app-footer">
        <div className="container row row--between">
          <span>Portfolio pages for creators.</span>
          <span className="mono">{host}</span>
        </div>
      </footer>
    </div>
  );
}

/**
 * A miniature of a published page, drawn entirely from the preset's own tokens
 * so the tiles cannot drift from what the editor actually produces.
 */
function SampleTile({ preset }: { preset: ThemePreset }) {
  const { tokens } = preset;
  // The tile only sells the idea if it is set in the real faces, and this is
  // the same loader the published page uses.
  useGoogleFonts(tokens);

  const display = {
    fontFamily: `'${tokens.displayFont}', Georgia, serif`,
    fontWeight: tokens.displayWeight,
    letterSpacing: tokens.displayTracking,
    textTransform: tokens.displayTransform,
  };

  return (
    <figure
      className="sample"
      style={{ background: tokens.bg, color: tokens.text, borderRadius: tokens.radius }}
    >
      <div className="sample__head">
        <span className="sample__name" style={display}>
          Your Name
        </span>
        <span
          className="sample__tagline"
          style={{ fontFamily: `'${tokens.bodyFont}', system-ui, sans-serif`, color: tokens.muted }}
        >
          Content Creator
        </span>
      </div>
      <div className="sample__grid" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((cell) => (
          <span key={cell} style={{ background: tokens.surface, borderRadius: tokens.radius }} />
        ))}
      </div>
      <figcaption
        className="sample__chip"
        style={{
          background: tokens.accent,
          color: tokens.onAccent,
          borderRadius: tokens.radius,
          fontFamily: `'${tokens.bodyFont}', system-ui, sans-serif`,
        }}
      >
        {preset.name}
      </figcaption>
    </figure>
  );
}

const LANDING_CSS = `
.landing-showcase {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: var(--app-space-4);
  margin-block: var(--app-space-5) var(--app-space-2);
}
.sample {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-3);
  margin: 0;
  padding: var(--app-space-4);
  border: 1px solid var(--app-border);
  box-shadow: var(--app-shadow-sm);
  min-height: 15rem;
}
.sample__head { display: flex; flex-direction: column; gap: 2px; }
.sample__name { font-size: 1.9rem; line-height: 1.05; }
.sample__tagline { font-size: 0.7rem; letter-spacing: 0.18em; text-transform: uppercase; }
.sample__grid {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.sample__grid > span { aspect-ratio: 9 / 16; display: block; }
.sample__chip {
  align-self: flex-start;
  padding: 0.2rem 0.7rem;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.landing-honest {
  list-style: none;
  margin-top: var(--app-space-3);
  color: var(--app-muted);
}
.landing-honest li { padding-left: 1.1rem; position: relative; }
.landing-honest li::before {
  content: '—';
  position: absolute;
  left: 0;
  color: var(--app-border-strong);
}
`;
